import { useTranslation } from "react-i18next";
import EmptyData from "../../../components/ui/EmptyData";
import Pagination from "../../../components/ui/Pagination";
import LabelCount from "../../../components/ui/LabelCount";
import {
  faTrashAlt,
  faEdit,
  faCopy,
  faEye,
} from "@fortawesome/pro-regular-svg-icons";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import axiosConfig from "../../../services/Axios";
import { Link } from "react-router-dom";
import ItemState from "../../../components/ui/ItemState";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PopoverMenu from "../../../components/ui/PopoverMenu";

function ItemsList({ items, loadItems, setFormData }) {
  const authUser = useSelector((state) => state.auth);
  const isAdmin = authUser?.user?.roles.includes("ROLE_ADMIN");
  const isOnlyUser = authUser?.user?.roles.includes("ROLE_USER") && !isAdmin;
  const { t } = useTranslation();

  const navigate = useNavigate();

  const handleDeleteItem = (item) => {
    if (item.itemOrders.length === 0) {
      Swal.fire({
        text: t("global.form.delete") + " " + item.name + " ?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("global.btn.confirm_delete"),
        cancelButtonText: t("global.btn.cancel"),
        customClass: {
          actions: "gap-2 h-20",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          try {
            axiosConfig
              .delete(process.env.REACT_APP_API_URL + item["@id"])
              .then((response) => {
                if (response?.status === 204) {
                  toast.success(t("settings.items.deleted"), {
                    theme: "colored",
                    hideProgressBar: true,
                  });
                  loadItems();
                }
              });
          } catch (e) {
            console.log(e);
          }
        }
      });
    } else {
      Swal.fire({
        text: t("items.message_delete_error", {
          count: item.itemOrders.length,
        }),
        icon: "warning",
        customClass: {
          actions: "gap-2 h-20",
        },
      });
    }
  };

  const handleDuplicateItem = (item) => {
    Swal.fire({
      text: t("global.form.duplicateQuestion", { name: item.name }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("global.btn.duplicate"),
      cancelButtonText: t("global.btn.cancel"),
      customClass: {
        actions: "gap-2 h-20",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        try {
          let data = { ...item };

          delete data["@id"];
          delete data["@type"];
          delete data.id;
          delete data.itemPackages;
          delete data.itemPictures;

          data.name = data.name + " " + "COPIE";
          data.itemCategory = data.itemCategory["@id"];
          data.itemSubCategory = data.itemSubCategory
            ? data.itemSubCategory["@id"]
            : null;
          data.logisticAddress = data.logisticAddress["@id"];
          data.partner = data.partner["@id"];
          data.state = "draft";

          axiosConfig
            .post(process.env.REACT_APP_API_URL + "/api/items", {
              ...data,
            })
            .then((response) => {
              if (response?.status === 201) {
                toast.success(t("settings.items.created"), {
                  theme: "colored",
                  hideProgressBar: true,
                });
                loadItems();
                navigate("/settings/items/" + response.data.id);
              }
            });
        } catch (e) {
          console.error(e);
          if (
            e.response?.status !== 201 &&
            e.response?.data["hydra:description"]
          ) {
            toast.error(e.response?.data["hydra:description"]);
          }
        }
      }
    });
  };

  return (
    <div>
      {items && items["hydra:member"].length > 0 ? (
        <>
          <div
            className={`bg-bioport-blue-light grid grid-cols-12 text-white font-bold px-4 p-3 text-sm`}
          >
            <div
              className={`${
                isAdmin ? "col-span-6 lg:col-span-4" : "col-span-6"
              }`}
            >
              {t("items.name")}
            </div>
            <div className="hidden lg:block text-center col-span-3">
              {t("categories.label")}
            </div>
            {isAdmin && (
              <div className="hidden lg:block text-center col-span-2">
                {t("items.partner")}
              </div>
            )}
            <div className="col-span-2 lg:col-span-1 text-center">
              {t("global.quantity")}
            </div>
            <div className="col-span-3 lg:col-span-1 text-center">
              {t("global.state")}
            </div>
            <div className="col-span-1 text-center"></div>
          </div>
          {items["hydra:member"].map((item, key) => (
            <div
              key={key}
              className={`grid grid-cols-12 px-4 p-3 hover:bg-gray-50 ${
                key % 2 === 0 && "bg-gray-100"
              }`}
            >
              <div
                className={`${
                  isAdmin ? "col-span-6 lg:col-span-4" : "col-span-6"
                } hover:underline`}
              >
                <Link to={"/settings/items/" + item.id}>{item.name}</Link>
              </div>
              <div className="hidden lg:block text-center col-span-3">
                {item.itemCategory.name}
                {item.itemSubCategory && " / " + item.itemSubCategory.name}
              </div>
              {isAdmin && (
                <div className="hidden lg:block text-center hover:underline col-span-2">
                  <Link to={"/settings/partners/" + item.partner.id}>
                    {item.partner.name}
                  </Link>
                </div>
              )}
              <div className="text-center col-span-2 lg:col-span-1">
                <LabelCount
                  count={item.totalQuantity}
                  defaultClass="bg-gray-300"
                  enabledClass="bg-bioport-blue text-white"
                />
              </div>
              <div className="text-center col-span-3 lg:col-span-1">
                <ItemState item={item} className={"px-3 py-1 text-xs"} />
              </div>
              <div className="text-center col-span-1">
                {isOnlyUser &&
                (item.state === "validated" || item.state === "online") ? (
                  <PopoverMenu
                    links={[
                      {
                        link: "/settings/items/" + item.id,
                        icon: faEye,
                        label: t("global.show"),
                        class:
                          "hover:cursor-pointer hover:underline text-bioport-blue mb-2",
                      },
                      {
                        link: () => handleDuplicateItem(item),
                        icon: faCopy,
                        label: t("global.btn.duplicate"),
                        class:
                          "hover:cursor-pointer hover:underline text-bioport-blue mb-2",
                      },
                    ]}
                  />
                ) : (
                  <PopoverMenu
                    links={[
                      {
                        link: "/settings/items/" + item.id,
                        icon: faEdit,
                        label: t("global.edit"),
                        class:
                          "hover:cursor-pointer hover:underline text-bioport-blue mb-2",
                      },
                      {
                        link: () => handleDuplicateItem(item),
                        icon: faCopy,
                        label: t("global.btn.duplicate"),
                        class:
                          "hover:cursor-pointer hover:underline text-bioport-blue mb-2",
                      },
                      {
                        link: () => handleDeleteItem(item),
                        icon: faTrashAlt,
                        label: t("global.form.delete"),
                        class:
                          "hover:cursor-pointer hover:underline text-red-600",
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          ))}
        </>
      ) : (
        <EmptyData className="mt-3">{t("global.no-result")}</EmptyData>
      )}
      {items &&
        items["hydra:member"] &&
        items["hydra:totalItems"] > items["hydra:member"].length &&
        items["hydra:view"] && (
          <Pagination
            data={items}
            onClick={(page) => {
              setFormData((formData) => ({
                ...formData,
                page: page,
              }));
            }}
          />
        )}
    </div>
  );
}

export default ItemsList;
